import React from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import {
    HAS_DOCUMENT,
    addWindowListener,
    removeWindowListener,
} from '@eventbrite/legacy-eds.utils/dom';
import { onClickOutsideHOC } from '@eventbrite/legacy-eds.utils/click';
import {
    HEAP_SHARE_EVENT_CLOSE_MODAL,
    reTrackHeapEvent,
} from '@eventbrite/legacy-eds.utils/tracking';
import { CloseButton } from './CloseButton';
import * as constants from './constants';
import { CollapsiblePaneLayout } from './CollapsiblePaneLayout';
import { ModalBody } from './ModalBody';
import styles from './Modal.module.scss';
import { injectIntl, type WrappedComponentProps } from 'react-intl';

const DecoratedModalBody = onClickOutsideHOC(
    ({
        children,
        onClose,
        headerActionIconType,
        headerActionTitle,
        onHeaderAction,
        title,
        secondaryTitle,
        illustration,
        pane,
        isClosable,
        isPaneOpen,
        onPaneClickOutside,
        paneInteraction,
        primarySize,
        primaryType,
        primaryText,
        primaryAttributes,
        primaryIsDisabled,
        onPrimaryAction,
        secondaryType,
        secondaryText,
        secondaryAttributes,
        secondaryIsDisabled,
        onSecondaryAction,
        tertiaryNode,
        type,
        onTertiaryAction,
        dangerouslySetContainerClassName,
        modalBodyIsEmpty,
        notificationOptions,
        buttonBarMessage,
        primaryIsLoading,
        secondaryIsLoading,
        closeButtonClassName,
        closeButtonCustomIcon,
    }) => {
        let dangerousClassName;

        if (dangerouslySetContainerClassName) {
            dangerousClassName = dangerouslySetContainerClassName.__className;
        }

        const className = classNames(styles.edsModalBody, {
            [styles.edsModalFade as string]: type !== constants.TYPE_TAKEOVER,
            [styles[`edsModal${type}`] as string]: !!type,
            [dangerousClassName as string]: dangerousClassName,
        });

        const bodyClassName = classNames(styles.edsModalContent, {
            [styles.edsModalBodyFadeIn as string]: type === constants.TYPE_TAKEOVER,
            [styles.edsModalBodyTakeoverPadding as string]: type === constants.TYPE_TAKEOVER,
        });

        let modalBody = (
            <CollapsiblePaneLayout
                isPaneOpen={isPaneOpen}
                onPaneClickOutside={onPaneClickOutside}
                pane={pane}
                paneInteraction={paneInteraction}
            >
                <ModalBody
                    headerActionIconType={headerActionIconType}
                    headerActionTitle={headerActionTitle}
                    onHeaderAction={onHeaderAction}
                    title={title}
                    secondaryTitle={secondaryTitle}
                    illustration={illustration}
                    primarySize={primarySize}
                    primaryType={primaryType}
                    primaryText={primaryText}
                    primaryAttributes={primaryAttributes}
                    primaryIsDisabled={primaryIsDisabled}
                    primaryIsLoading={primaryIsLoading}
                    onPrimaryAction={onPrimaryAction}
                    secondaryType={secondaryType}
                    secondaryText={secondaryText}
                    secondaryAttributes={secondaryAttributes}
                    secondaryIsDisabled={secondaryIsDisabled}
                    secondaryIsLoading={secondaryIsLoading}
                    onSecondaryAction={onSecondaryAction}
                    tertiaryNode={tertiaryNode}
                    onTertiaryAction={onTertiaryAction}
                    buttonBarMessage={buttonBarMessage}
                >
                    {children}
                </ModalBody>
            </CollapsiblePaneLayout>
        );

        if (modalBodyIsEmpty) {
            modalBody = children;
        }

        const childrenEventId = children?.props?.shareOptions?.eventId || null;
        const childrenAffCode = children?.props?.shareOptions?.affCode || null;

        const onCloseHandler = onClose
            ? (e) => {
                  if (childrenEventId) {
                      reTrackHeapEvent({
                          affCode: childrenAffCode,
                          heapEventName: HEAP_SHARE_EVENT_CLOSE_MODAL,
                          eventId: childrenEventId,
                      });
                  }
                  onClose(e);
              }
            : undefined;

        return (
            <div className={className} aria-hidden="false" data-spec="eds-modal-body">
                <div className={bodyClassName}>
                    {modalBody}

                    {isClosable && (
                        <CloseButton
                            customClassName={closeButtonClassName}
                            customIcon={closeButtonCustomIcon}
                            hasBackgroundColor={type === constants.ModalType.aperture}
                            onClose={onCloseHandler}
                            type={type}
                        />
                    )}
                </div>
            </div>
        );
    },
);

interface ModalProps {
    children: React.ReactNode;
    type?: string;
    isShown: boolean;
    noMinHeight?: boolean;
    isClosable?: boolean;
    noPadding?: boolean;
    onClose?: () => void;
    headerActionIconType?: string;
    headerActionTitle?: string;
    onHeaderAction?: () => void;
    title?: React.ReactNode;
    secondaryTitle?: React.ReactNode;
    illustration?: React.ReactNode;
    pane?: React.ReactNode;
    isPaneOpen?: boolean;
    onPaneClickOutside?: () => void;
    paneInteraction?: string;
    primarySize?: string;
    primaryType?: string;
    primaryText?: React.ReactNode;
    primaryAttributes?: Record<string, any>;
    primaryIsDisabled?: boolean;
    onPrimaryAction?: () => void;
    secondaryType?: string;
    secondaryText?: React.ReactNode;
    secondaryAttributes?: Record<string, any>;
    secondaryIsDisabled?: boolean;
    onSecondaryAction?: () => void;
    tertiaryNode?: React.ReactNode;
    onTertiaryAction?: () => void;
    dangerouslySetContainerClassName?: { __className: string };
    modalBodyIsEmpty?: boolean;
    notificationOptions?: any;
    buttonBarMessage?: string;
    ariaLabel?: string;
    ariaLabelledby?: string;
    ariaDescribedby?: string;
    ariaModal?: boolean;
    primaryIsLoading?: boolean;
    secondaryIsLoading?: boolean;
    __containerClassName?: string;
    closeButtonClassName?: string;
    closeButtonCustomIcon?: React.ReactNode;
    disableFocusTrap?: boolean;
    focusTrapOptions?: Record<string, any>;
    disableCloseOnClickOutside?: boolean;
    isPortal?: boolean;
    modalHostNodeSelector?: string;
    roleIsDialog?: boolean;
}

class ModalComponent extends React.PureComponent<ModalProps & WrappedComponentProps> {
    private _boundHandleEscKey!: (event: KeyboardEvent) => void;

    static defaultProps = {
        type: constants.TYPE_SIMPLE,
        isClosable: true,
        isShown: false,
        noPadding: false,
        roleIsDialog: false,
        modalHostNodeSelector: constants.ROOT_NODE_SELECTOR,
    };

    componentDidMount() {
        const { isShown } = this.props;

        this._boundHandleEscKey = this._handleEscKey.bind(this);
        this._checkShownState(isShown);
    }

    UNSAFE_componentWillReceiveProps({ isShown }) {
        this._checkShownState(isShown);
    }

    componentWillUnmount() {
        this._unattachWindowListeners();
        this._lockModalViewport(false);
    }

    // used to lock the viewport so that the user can scroll under the modal
    _lockModalViewport(isLocked) {
        if (HAS_DOCUMENT) {
            const lockClass = styles.edsModalHiddenOverflow as string;

            if (isLocked) {
                document.body?.classList.add(lockClass);
            } else {
                document.body?.classList.remove(lockClass);
            }
        }
    }

    _checkShownState(isShown) {
        this._lockModalViewport(isShown);

        if (isShown) {
            this._attachWindowListeners();
        } else {
            this._unattachWindowListeners();
        }
    }

    _attachWindowListeners() {
        addWindowListener('keydown', this._boundHandleEscKey);
    }

    _unattachWindowListeners() {
        removeWindowListener('keydown', this._boundHandleEscKey);
    }

    _handleClose() {
        if (this.props.onClose) {
            this.props.onClose();
        }
    }

    _handleEscKey(event) {
        if (event.key === 'Escape') {
            this._handleClose();
        }
    }

    _handleClickOutside() {
        const { disableCloseOnClickOutside } = this.props;

        if (!disableCloseOnClickOutside) {
            this._handleClose();
        }
    }

    render() {
        const {
            intl,
            children,
            type,
            isShown,
            noMinHeight,
            title,
            secondaryTitle,
            illustration,
            pane,
            isClosable,
            isPaneOpen,
            onPaneClickOutside,
            paneInteraction,
            isPortal,
            headerActionIconType,
            headerActionTitle,
            onHeaderAction,
            primarySize,
            primaryType,
            primaryText,
            primaryAttributes,
            primaryIsDisabled,
            onPrimaryAction,
            secondaryType,
            secondaryText,
            secondaryAttributes,
            secondaryIsDisabled,
            onSecondaryAction,
            tertiaryNode,
            onTertiaryAction,
            onClose,
            noPadding,
            dangerouslySetContainerClassName,
            modalBodyIsEmpty,
            modalHostNodeSelector,
            notificationOptions,
            roleIsDialog,
            ariaLabel,
            ariaLabelledby,
            ariaDescribedby,
            ariaModal,
            buttonBarMessage,
            primaryIsLoading,
            secondaryIsLoading,
            __containerClassName,
            closeButtonClassName,
            closeButtonCustomIcon,
            disableFocusTrap,
        } = this.props;

        if (!isShown) {
            return null;
        }

        const modalClasses = classNames(styles.edsModal, __containerClassName, {
            [styles.edsModalFade as string]: type !== constants.TYPE_TAKEOVER,
            [styles[`edsModal${type}`] as string]: type,
            [styles.edsModalActive as string]: isShown,
            [styles.edsModalHasPadding as string]: !noPadding,
            [styles.edsModalSetHeight as string]: !noMinHeight && type !== constants.TYPE_TAKEOVER,
        });

        // We need to set a fallbackFocus in case there was no inital foucsed element
        // https://github.com/davidtheclark/focus-trap/blob/master/index.js#L156
        const focusTrapOptions = {
            fallbackFocus: () => document.body,
            ...this.props.focusTrapOptions,
        };
        let accessibilityProps = {
            'aria-label': ariaLabel,
            'aria-labelledby': ariaLabelledby,
            'aria-describedby': ariaDescribedby,
            'aria-modal': ariaModal,
            role: 'alertdialog',
        };

        // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-labelledby_attribute
        if (title && !ariaLabelledby) {
            accessibilityProps = {
                ...accessibilityProps,
                'aria-labelledby': constants.MODAL_TITLE_ID,
            };
        }

        // Default to alertdialog role type unless specifically dialog type is requested
        // https://www.w3.org/TR/wai-aria-1.1/#alertdialog & https://www.w3.org/TR/wai-aria-1.1/#dialog
        if (roleIsDialog) {
            accessibilityProps = { ...accessibilityProps, role: 'dialog' };
        }

        const modalContent = (
            <div
                className={modalClasses}
                data-spec="eds-modal"
                {...accessibilityProps}
                aria-label={
                    ariaLabel ||
                    intl.formatMessage({
                        id: 'Date Picker Dialog',
                        defaultMessage: 'Date Picker Dialog',
                    })
                }
            >
                <DecoratedModalBody
                    isClosable={isClosable}
                    onClose={onClose}
                    primarySize={primarySize}
                    primaryType={primaryType}
                    primaryText={primaryText}
                    primaryAttributes={primaryAttributes}
                    primaryIsDisabled={primaryIsDisabled}
                    primaryIsLoading={primaryIsLoading}
                    onPrimaryAction={onPrimaryAction}
                    secondaryType={secondaryType}
                    secondaryText={secondaryText}
                    secondaryAttributes={secondaryAttributes}
                    secondaryIsDisabled={secondaryIsDisabled}
                    secondaryIsLoading={secondaryIsLoading}
                    tertiaryNode={tertiaryNode}
                    type={type}
                    onTertiaryAction={onTertiaryAction}
                    onSecondaryAction={onSecondaryAction}
                    headerActionIconType={headerActionIconType}
                    headerActionTitle={headerActionTitle}
                    onHeaderAction={onHeaderAction}
                    title={title}
                    secondaryTitle={secondaryTitle}
                    illustration={illustration}
                    pane={pane}
                    isPaneOpen={isPaneOpen}
                    onPaneClickOutside={onPaneClickOutside}
                    paneInteraction={paneInteraction}
                    onClickOutside={this._handleClickOutside.bind(this)}
                    dangerouslySetContainerClassName={dangerouslySetContainerClassName}
                    modalBodyIsEmpty={modalBodyIsEmpty}
                    notificationOptions={notificationOptions}
                    buttonBarMessage={buttonBarMessage}
                    closeButtonClassName={closeButtonClassName}
                    closeButtonCustomIcon={closeButtonCustomIcon}
                >
                    {children}
                </DecoratedModalBody>
            </div>
        );

        const modalComponent = disableFocusTrap ? (
            modalContent
        ) : (
            <FocusTrap focusTrapOptions={focusTrapOptions} data-spec="focus-trap-component">
                {modalContent}
            </FocusTrap>
        );

        const portalHostNode =
            isPortal && modalHostNodeSelector && document.querySelector(modalHostNodeSelector);

        return portalHostNode
            ? ReactDOM.createPortal(modalComponent, portalHostNode)
            : modalComponent;
    }
}

export const Modal = injectIntl(ModalComponent);
